import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { getSupabaseBrowserClient } from '../lib/supabase'
import { assignConfirmation, requestConfirmationInformation } from './assessment-api'
import { toCustomerSafeRpcError } from './customer-safe-operation-error'

export type ContextualMemberOption = { membershipId: string; displayName: string }

type RpcError = { message: string }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse
type FunctionInvokeResult = { data: unknown; error: { message?: string } | null }

type AssignmentState = {
  assignedMembershipId: string | null
  invitation: null | {
    invitationId: string
    displayName: string
    email: string
    status: string
    expiresAt: string
  }
}

type InviteResult = {
  invitationId?: string
  targetId?: string
  displayName?: string
  delivery?: string
  warning?: string
  error?: string
}

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}
}
function text(value: unknown) { return typeof value === 'string' ? value : '' }

async function loadAssignmentState(requirementId: string): Promise<AssignmentState> {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { data, error } = await client.rpc('get_r1_confirmation_assignment_state', { p_requirement_id: requirementId })
  if (error) {
    console.error('Supabase RPC get_r1_confirmation_assignment_state failed', error)
    throw toCustomerSafeRpcError('get_r1_confirmation_assignment_state', error)
  }
  const row = object(data)
  const invitation = row.invitation ? object(row.invitation) : null
  return {
    assignedMembershipId: text(row.assignedMembershipId) || null,
    invitation: invitation ? {
      invitationId: text(invitation.invitationId),
      displayName: text(invitation.displayName),
      email: text(invitation.email),
      status: text(invitation.status),
      expiresAt: text(invitation.expiresAt),
    } : null,
  }
}

async function inviteContextualPerson(input: {
  targetType: 'specialist_confirmation' | 'information_request' | 'approval_requirement'
  targetId: string
  displayName: string
  email: string
  requestText?: string
}): Promise<InviteResult> {
  const client = getSupabaseBrowserClient()
  const invocation = await client.functions.invoke('invite-contextual-governance-user', { body: input }) as unknown as FunctionInvokeResult
  if (invocation.error) {
    console.error('Contextual governance invitation failed', invocation.error)
    throw new Error('Unable to send the governed invitation. Please try again.')
  }
  const result = object(invocation.data)
  if (result.error) {
    const serverMessage = text(result.error)
    console.error('Contextual governance invitation returned an error', serverMessage)
    throw toCustomerSafeRpcError('invite_contextual_governance_user', { message: serverMessage })
  }
  return {
    invitationId: text(result.invitationId) || undefined,
    targetId: text(result.targetId) || undefined,
    displayName: text(result.displayName) || undefined,
    delivery: text(result.delivery) || undefined,
    warning: text(result.warning) || undefined,
  }
}

type ApprovalAssignmentState = AssignmentState & { members: ContextualMemberOption[] }

async function loadApprovalAssignmentState(requirementId: string): Promise<ApprovalAssignmentState> {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { data, error } = await client.rpc('get_r1_approval_assignment_state', { p_requirement_id: requirementId })
  if (error) {
    console.error('Supabase RPC get_r1_approval_assignment_state failed', error)
    throw toCustomerSafeRpcError('get_r1_approval_assignment_state', error)
  }
  const row = object(data)
  const invitation = row.invitation ? object(row.invitation) : null
  return {
    assignedMembershipId: text(row.assignedMembershipId) || null,
    members: Array.isArray(row.members) ? row.members.map(object).map((member) => ({ membershipId: text(member.membershipId), displayName: text(member.displayName) })).filter((member) => member.membershipId) : [],
    invitation: invitation ? {
      invitationId: text(invitation.invitationId), displayName: text(invitation.displayName), email: text(invitation.email), status: text(invitation.status), expiresAt: '',
    } : null,
  }
}

async function assignApproval(requirementId: string, membershipId: string) {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { error } = await client.rpc('assign_r1_approval_requirement', { p_requirement_id: requirementId, p_membership_id: membershipId })
  if (error) {
    console.error('Supabase RPC assign_r1_approval_requirement failed', error)
    throw toCustomerSafeRpcError('assign_r1_approval_requirement', error)
  }
}

export function ApprovalAssigneePicker({ requirementId, onChanged }: { requirementId: string; onChanged: () => Promise<void> }) {
  const [membershipId, setMembershipId] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [message, setMessage] = useState<string | null>(null)
  const assignmentQuery = useQuery({ queryKey: ['r1-approval-assignment-state', requirementId], queryFn: () => loadApprovalAssignmentState(requirementId) })
  const assignMutation = useMutation({
    mutationFn: () => assignApproval(requirementId, membershipId),
    onSuccess: async () => { setMembershipId(''); setMessage('Named approver assigned. The approval type and decision order remain unchanged.'); await onChanged(); await assignmentQuery.refetch() },
  })
  const inviteMutation = useMutation({
    mutationFn: () => inviteContextualPerson({ targetType: 'approval_requirement', targetId: requirementId, displayName: inviteName, email: inviteEmail }),
    onSuccess: async (result) => { setMessage(result.warning ?? `Invitation sent to ${inviteName}. This approval remains blocked until they securely join the organisation.`); setInviteName(''); setInviteEmail(''); await onChanged(); await assignmentQuery.refetch() },
  })
  const manageMutation = useMutation({
    mutationFn: (operation: 'resend' | 'cancel') => manageContextualInvitation({ operation, targetType: 'approval_requirement', targetId: requirementId }),
    onSuccess: async (result) => {
      setMessage(result.operation === 'cancel'
        ? 'Invitation cancelled for this approval. The governed requirement is unchanged; choose another named person when ready.'
        : result.warning ?? 'Invitation resent. This approval remains blocked until the invited person securely joins.')
      await onChanged()
      await assignmentQuery.refetch()
    },
  })
  const pending = assignmentQuery.data?.invitation
  if (pending?.status === 'pending' && !assignmentQuery.data?.assignedMembershipId) {
    return <div className="form-stack contextual-assignment-pending" aria-live="polite">
      <p><strong>Waiting for {pending.displayName || pending.email} to join Swell.</strong></p>
      <p className="muted">This person has been invited for this specific approval. The approval type, sequence and separation-of-duties checks remain in force.</p>
      <div className="button-row">
        <button className="button secondary" type="button" disabled={manageMutation.isPending} onClick={() => manageMutation.mutate('resend')}>
          {manageMutation.isPending && manageMutation.variables === 'resend' ? 'Resending…' : 'Resend invitation'}
        </button>
      </div>
      <p className="muted">An Organisation Administrator can use Recall assignment below to clear this pending approver without changing the governed requirement.</p>
      {message ? <div className="inline-alert neutral">{message}</div> : null}
      {manageMutation.error ? <div className="inline-alert danger" role="alert">{manageMutation.error instanceof Error ? manageMutation.error.message : 'Unable to update the approval invitation.'}</div> : null}
    </div>
  }
  const error = assignmentQuery.error ?? assignMutation.error ?? inviteMutation.error ?? manageMutation.error
  return <div className="form-stack contextual-person-picker">
    <label>Person responsible for this approval
      <select value={membershipId} onChange={(event) => setMembershipId(event.target.value)}><option value="">Choose an organisation member…</option>{(assignmentQuery.data?.members ?? []).map((member) => <option key={member.membershipId} value={member.membershipId}>{member.displayName}</option>)}</select>
    </label>
    <button className="button secondary" type="button" disabled={!membershipId || assignMutation.isPending} onClick={() => assignMutation.mutate()}>{assignMutation.isPending ? 'Assigning…' : 'Assign person'}</button>
    <details className="contextual-invite-person"><summary>Invite someone</summary><div className="form-stack"><p>The invitation identifies this approval but does not grant authority before secure sign-in and membership activation.</p><label>Name<input value={inviteName} onChange={(event) => setInviteName(event.target.value)} autoComplete="name" /></label><label>Work email address<input type="email" value={inviteEmail} onChange={(event) => setInviteEmail(event.target.value)} autoComplete="email" /></label><button className="button secondary" type="button" disabled={!inviteName.trim() || !inviteEmail.includes('@') || inviteMutation.isPending} onClick={() => inviteMutation.mutate()}>{inviteMutation.isPending ? 'Sending…' : 'Send governed invitation'}</button></div></details>
    {message ? <div className="inline-alert success" aria-live="polite">{message}</div> : null}
    {error ? <div className="inline-alert danger" role="alert">{error instanceof Error ? error.message : 'Unable to update the approval assignment.'}</div> : null}
  </div>
}

export async function manageContextualInvitation(input: {
  operation: 'resend' | 'cancel'
  targetType: 'specialist_confirmation' | 'information_request' | 'approval_requirement'
  targetId: string
}): Promise<InviteResult & { operation?: string }> {
  const client = getSupabaseBrowserClient()
  const invocation = await client.functions.invoke('invite-contextual-governance-user', { body: input }) as unknown as FunctionInvokeResult
  if (invocation.error) {
    console.error('Contextual governance invitation operation failed', invocation.error)
    throw new Error('Unable to update the governed invitation. Refresh and try again.')
  }
  const result = object(invocation.data)
  if (result.error) {
    const serverMessage = text(result.error)
    console.error('Contextual governance invitation operation returned an error', serverMessage)
    throw toCustomerSafeRpcError('manage_contextual_governance_invitation', { message: serverMessage })
  }
  return {
    invitationId: text(result.invitationId) || undefined,
    targetId: text(result.targetId) || undefined,
    delivery: text(result.delivery) || undefined,
    warning: text(result.warning) || undefined,
    operation: text(result.operation) || input.operation,
  }
}

export function ConfirmationAssigneePicker({
  requirementId,
  members,
  onChanged,
}: {
  requirementId: string
  members: ContextualMemberOption[]
  onChanged: () => Promise<void>
}) {
  const [membershipId, setMembershipId] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [message, setMessage] = useState<string | null>(null)

  const assignmentQuery = useQuery({
    queryKey: ['r1-confirmation-assignment-state', requirementId],
    queryFn: () => loadAssignmentState(requirementId),
  })
  const assignMutation = useMutation({
    mutationFn: () => assignConfirmation(requirementId, membershipId),
    onSuccess: async () => {
      setMembershipId('')
      setMessage(null)
      await onChanged()
      await assignmentQuery.refetch()
    },
  })
  const inviteMutation = useMutation({
    mutationFn: () => inviteContextualPerson({
      targetType: 'specialist_confirmation',
      targetId: requirementId,
      displayName: inviteName,
      email: inviteEmail,
    }),
    onSuccess: async (result) => {
      setMessage(result.warning ?? `Invitation sent to ${inviteName}. The confirmation will become actionable only after they sign in and join the organisation.`)
      setInviteName('')
      setInviteEmail('')
      await onChanged()
      await assignmentQuery.refetch()
    },
  })
  const manageMutation = useMutation({
    mutationFn: (operation: 'resend' | 'cancel') => manageContextualInvitation({
      operation,
      targetType: 'specialist_confirmation',
      targetId: requirementId,
    }),
    onSuccess: async (result) => {
      setMessage(result.operation === 'cancel'
        ? 'Invitation cancelled for this confirmation. Its governed outcome is unchanged; choose another person when ready.'
        : result.warning ?? 'Invitation resent. The confirmation remains blocked until the invited person securely joins.')
      await onChanged()
      await assignmentQuery.refetch()
    },
  })

  const pending = assignmentQuery.data?.invitation
  if (pending?.status === 'pending' && !assignmentQuery.data?.assignedMembershipId) {
    return <div className="form-stack contextual-assignment-pending" aria-live="polite">
      <p><strong>Waiting for {pending.displayName || pending.email} to join Swell.</strong></p>
      <p className="muted">Their invitation is linked to this specialist confirmation. The request remains blocked until authentication activates their organisation membership.</p>
      <div className="button-row">
        <button className="button secondary" type="button" disabled={manageMutation.isPending} onClick={() => manageMutation.mutate('resend')}>
          {manageMutation.isPending && manageMutation.variables === 'resend' ? 'Resending…' : 'Resend invitation'}
        </button>
        <button className="button secondary" type="button" disabled={manageMutation.isPending} onClick={() => manageMutation.mutate('cancel')}>
          {manageMutation.isPending && manageMutation.variables === 'cancel' ? 'Cancelling…' : 'Cancel invitation'}
        </button>
      </div>
      {message ? <div className="inline-alert neutral">{message}</div> : null}
      {manageMutation.error ? <div className="inline-alert danger" role="alert">{manageMutation.error instanceof Error ? manageMutation.error.message : 'Unable to update the invitation.'}</div> : null}
    </div>
  }

  const error = assignMutation.error ?? inviteMutation.error ?? assignmentQuery.error
  return <div className="form-stack contextual-person-picker">
    <label>Person responsible for this confirmation
      <select value={membershipId} onChange={(event) => setMembershipId(event.target.value)}>
        <option value="">Choose an organisation member…</option>
        {members.map((member) => <option key={member.membershipId} value={member.membershipId}>{member.displayName}</option>)}
      </select>
    </label>
    <button className="button secondary" type="button" disabled={!membershipId || assignMutation.isPending} onClick={() => assignMutation.mutate()}>
      {assignMutation.isPending ? 'Assigning…' : 'Assign person'}
    </button>
    <details className="contextual-invite-person">
      <summary>Invite someone</summary>
      <div className="form-stack">
        <p>Use this when the person is not yet in your organisation. The invitation is linked to this confirmation and does not grant authority before sign-in.</p>
        <label>Name<input value={inviteName} onChange={(event) => setInviteName(event.target.value)} autoComplete="name" /></label>
        <label>Work email address<input type="email" value={inviteEmail} onChange={(event) => setInviteEmail(event.target.value)} autoComplete="email" /></label>
        <button className="button secondary" type="button" disabled={!inviteName.trim() || !inviteEmail.includes('@') || inviteMutation.isPending} onClick={() => inviteMutation.mutate()}>
          {inviteMutation.isPending ? 'Sending…' : 'Send governed invitation'}
        </button>
      </div>
    </details>
    {message ? <div className="inline-alert success" aria-live="polite">{message}</div> : null}
    {error ? <div className="inline-alert danger" role="alert">{error instanceof Error ? error.message : 'Unable to update the assignment.'}</div> : null}
  </div>
}

export function InformationRequestComposer({
  requirementId,
  members,
  onChanged,
}: {
  requirementId: string
  members: ContextualMemberOption[]
  onChanged: () => Promise<void>
}) {
  const [membershipId, setMembershipId] = useState('')
  const [requestText, setRequestText] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteEmail, setInviteEmail] = useState('')
  const [message, setMessage] = useState<string | null>(null)

  const requestMutation = useMutation({
    mutationFn: () => requestConfirmationInformation(requirementId, membershipId, requestText),
    onSuccess: async () => {
      setMembershipId('')
      setRequestText('')
      setMessage(null)
      await onChanged()
    },
  })
  const inviteMutation = useMutation({
    mutationFn: () => inviteContextualPerson({
      targetType: 'information_request',
      targetId: requirementId,
      displayName: inviteName,
      email: inviteEmail,
      requestText,
    }),
    onSuccess: async (result) => {
      setMessage(result.warning ?? `Invitation sent to ${inviteName}. The information request remains blocked until they sign in and join the organisation.`)
      setMembershipId('')
      setRequestText('')
      setInviteName('')
      setInviteEmail('')
      await onChanged()
    },
  })

  const error = requestMutation.error ?? inviteMutation.error

  return <div className="form-stack information-request-create-form contextual-person-picker">
    <p>Send a focused question to a named person in your organisation, or invite them from here if they have not joined Swell yet. Your confirmation remains blocked until all open requests are resolved.</p>
    <label>Ask
      <select value={membershipId} onChange={(event) => setMembershipId(event.target.value)}>
        <option value="">Choose an organisation member…</option>
        {members.map((member) => <option key={member.membershipId} value={member.membershipId}>{member.displayName}</option>)}
      </select>
    </label>
    <label>What do you need to know?
      <textarea rows={4} value={requestText} onChange={(event) => setRequestText(event.target.value)} />
    </label>
    <button className="button secondary" type="button" disabled={!membershipId || !requestText.trim() || requestMutation.isPending} onClick={() => requestMutation.mutate()}>
      {requestMutation.isPending ? 'Sending…' : 'Request information'}
    </button>
    <details className="contextual-invite-person">
      <summary>Invite someone</summary>
      <div className="form-stack">
        <p>The invitation email identifies this governed question but reveals no organisation data before sign-in.</p>
        <label>Name<input value={inviteName} onChange={(event) => setInviteName(event.target.value)} autoComplete="name" /></label>
        <label>Work email address<input type="email" value={inviteEmail} onChange={(event) => setInviteEmail(event.target.value)} autoComplete="email" /></label>
        <button className="button secondary" type="button" disabled={!inviteName.trim() || !inviteEmail.includes('@') || !requestText.trim() || inviteMutation.isPending} onClick={() => inviteMutation.mutate()}>
          {inviteMutation.isPending ? 'Sending…' : 'Invite and request information'}
        </button>
      </div>
    </details>
    {message ? <div className="inline-alert success" aria-live="polite">{message}</div> : null}
    {error ? <div className="inline-alert danger" role="alert">{error instanceof Error ? error.message : 'Unable to create the information request.'}</div> : null}
  </div>
}
